import { createFileRoute, Link, notFound, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { setMediumCache } from "@/lib/page-cache";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import { getProvider, findInsurance, searchProviders, trackListingMetric, getProviderReviews, submitReview, deleteReview, cleanProviderForDisplay, specialtyToTitle, type Provider, type Review } from "@/data/heallexa";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
 CheckCircle2, MapPin, Phone, Globe, Star, Flag,
 Calendar, Navigation, ShieldCheck, Building2, ChevronLeft, ChevronRight, Trash2,
 Linkedin, Facebook, Instagram, Youtube, ExternalLink,
} from "lucide-react";
import { Calendar as ShadCalendar } from "@/components/ui/calendar";
import { type AvailabilityWindow } from "@/components/AvailabilityStrip";
import { BookingDialog } from "@/components/BookingDialog";
import { ProviderTrustFunnel } from "@/components/seo/ProviderTrustFunnel";
import { cn } from "@/lib/utils";
import { buildProviderSlug, parseProviderParam } from "@/lib/provider-slug";
import { providerSchemaType } from "@/lib/seo-schema";
import { providerTitle, providerDescription, stripTerminalCredentials } from "@/lib/seo-meta";
import { stateSlugFromCode } from "@/lib/seo-links";
import {
  getProviderByPublicIdFn,
  getProviderByIdPrefixFn,
  loadProviderAvailabilityFn,
  trackSocialClickFn,
  createBookingFn,
} from "./-providers.$id.functions";

function dedupeCredentials(name: string, credentials: string | null | undefined): string | null {
  if (!credentials) return null;
  const tokens = credentials.split(/[,/]/).map((s) => s.trim()).filter(Boolean);
  const unique = tokens.filter((t) => !name.toLowerCase().includes(`, ${t.toLowerCase()}`));
  return unique.length > 0 ? unique.join(", ") : null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeUrl(u?: string | null): string | null {
 if (!u) return null;
 const s = u.trim();
 if (!s) return null;
 if (/^https?:\/\//i.test(s)) return s;
 return `https://${s.replace(/^\/+/, "")}`;
}

function normalizePhoneToE164(raw: string | null | undefined): string | null {
 if (!raw) return null;
 const digits = raw.replace(/\D/g, "");
 if (digits.length === 10) return `+1${digits}`;
 if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
 return null;
}

const FALLBACK_OG_IMAGE = "https://heallexa.com/og-image.jpg";

const TikTokIcon = (props: React.SVGProps<SVGSVGElement>) => (
 <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
 <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52V7.09a4.85 4.85 0 0 1-1.84-.4z"/>
 </svg>
);

const XIcon = (props: React.SVGProps<SVGSVGElement>) => (
 <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
 <path d="M18.244 2H21.5l-7.49 8.56L22.5 22h-6.79l-5.32-6.96L4.32 22H1.06l8.02-9.16L1.5 2h6.95l4.81 6.36L18.244 2zm-2.38 18h1.88L7.22 4h-2L15.864 20z"/>
 </svg>
);

function providerErrorRecoveryKey() {
 if (typeof window === "undefined") return "heallexa_provider_error_recovered";
 return `heallexa_provider_error_recovered:${window.location.pathname}`;
}

function ProviderErrorBoundary({ error, reset }: { error: Error; reset: () => void }) {
 const router = useRouter();
 const [attemptedRecovery, setAttemptedRecovery] = useState(() => {
 if (typeof window === "undefined") return true;
 return sessionStorage.getItem(providerErrorRecoveryKey()) === "1";
 });

 useEffect(() => {
 if (attemptedRecovery) return;
 setAttemptedRecovery(true);
 if (typeof window !== "undefined") sessionStorage.setItem(providerErrorRecoveryKey(), "1");
 Promise.resolve(router.invalidate()).finally(reset);
 }, [attemptedRecovery, reset, router]);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-2xl px-6 pt-36 pb-20 text-center">
 <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">
 Provider page
 </p>
 <h1 className="text-2xl font-semibold text-foreground mb-3">
 {attemptedRecovery ? "We couldn't load this provider page" : "Reloading this provider page\u2026"}
 </h1>
 <p className="text-sm text-muted-foreground leading-relaxed mb-6">
 {attemptedRecovery
 ? "This profile hit a temporary browser error. Please reload the page or continue browsing providers."
 : "This profile hit a temporary browser error. We're refreshing it automatically."}
 </p>
 {attemptedRecovery && (
 <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
 <Button onClick={() => window.location.reload()} className="rounded-none">Reload page</Button>
 <Button asChild variant="outline" className="rounded-none"><Link to="/browse">Back to browse</Link></Button>
 </div>
 )}
 <p className="sr-only">{error.message}</p>
 </main>
 </div>
 );
}

export const Route = createFileRoute("/providers/$id")({
 staleTime: 10 * 60_000,
 loader: async ({ params }) => {
 await setMediumCache();
 const parsed = parseProviderParam(params.id);
 if (parsed.kind === "invalid") throw notFound();

 let provider: Provider | null = null;
 if (parsed.kind === "uuid") {
 provider = await getProvider(parsed.uuid);
 if (!provider) throw notFound();
 const slug = buildProviderSlug(provider);
 if (slug && slug !== params.id) {
 throw redirect({ to: "/providers/$id", params: { id: slug }, statusCode: 301 });
 }
 } else {
 let row: any = null;
 if (parsed.kind === "public_id") {
 row = await getProviderByPublicIdFn({ data: { publicId: parsed.publicId } });
 } else {
 row = await getProviderByIdPrefixFn({ data: { prefix: parsed.shortId } });
 }
 if (!row) throw notFound();
 provider = cleanProviderForDisplay(row as Provider);
 const canonicalSlug = buildProviderSlug(provider);
 if (canonicalSlug && canonicalSlug !== params.id) {
 throw redirect({ to: "/providers/$id", params: { id: canonicalSlug }, statusCode: 301 });
 }
 }

 return {
 provider,
 related: [] as Provider[],
 };
 },

 head: ({ loaderData }) => {
 const p = loaderData?.provider;
 const pa = p as any;
 const slug = p ? buildProviderSlug(p) : "";
 const url = p ? `https://heallexa.com/providers/${slug}` : "https://heallexa.com/browse";

 const credentials = p ? (pa.credentials as string | null | undefined) : null;
 const cleanName = p ? stripTerminalCredentials(p.name) : "Provider";
 const nameWithCreds = credentials ? `${cleanName}, ${credentials}` : cleanName;

 const title = p ? providerTitle({
  name: p.name,
  credentials: pa.credentials as string | null | undefined,
  specialty: p.specialty,
  city: p.city,
  state: p.state,
 }) : "Provider | Heallexa";

 const description = p ? providerDescription({
  name: p.name,
  credentials: pa.credentials as string | null | undefined,
  specialty: p.specialty,
  city: p.city,
  state: p.state,
  address: p.address,
  zip: pa.zip as string | null | undefined,
  phone: p.phone,
 }) : "Healthcare provider profile on Heallexa.";

 const photo = p
  ? (pa.display_photo_url ?? pa.profile_photo_url ?? pa.avatar_url ?? null)
  : null;
 const ogImage = photo ?? FALLBACK_OG_IMAGE;
 const ogImageAlt = p
  ? `${nameWithCreds} — ${p.specialty} in ${p.city}${p.state ? `, ${p.state}` : ""}`
  : "Healthcare provider on Heallexa";

 const nameParts = cleanName.replace(/^Dr\.\s*/i, "").split(/[\s,]+/).filter(Boolean);
 const profileFirst = nameParts[0] ?? null;
 const profileLast = nameParts.length > 1 ? nameParts[nameParts.length - 1] : null;

 const meta: Array<Record<string, string>> = [
  { title },
  { name: "description", content: description },
  { name: "robots", content: "index, follow" },
  { property: "og:title", content: title },
  { property: "og:description", content: description },
  { property: "og:url", content: url },
  { property: "og:type", content: "profile" },
  { property: "og:image", content: ogImage },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { property: "og:image:alt", content: ogImageAlt },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: title },
  { name: "twitter:description", content: description },
  { name: "twitter:image", content: ogImage },
 ];
 if (profileFirst) meta.push({ property: "profile:first_name", content: profileFirst });
 if (profileLast) meta.push({ property: "profile:last_name", content: profileLast });

 const citySlug = p ? p.city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") : "";
 const specSlug = p ? p.specialty.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") : "";

 const localReviewCount = Number(pa?.reviews_count ?? 0);
 const googleReviewCount = Number(p?.google_review_count ?? 0);
 const useLocal = p?.rating != null && localReviewCount > 0;
 const useGoogle = !useLocal && p?.google_rating != null && googleReviewCount > 0;
 const aggregateRating = useLocal
  ? { "@type": "AggregateRating", ratingValue: Number(p!.rating), reviewCount: localReviewCount, bestRating: 5, worstRating: 1 }
  : useGoogle
  ? { "@type": "AggregateRating", ratingValue: Number(p!.google_rating), reviewCount: googleReviewCount, bestRating: 5, worstRating: 1 }
  : undefined;

 const services: string[] = Array.isArray(p?.services)
  ? (p!.services as unknown[]).filter((s): s is string => typeof s === "string" && s.trim().length > 0)
  : [];

 const npi = pa?.npi as string | null | undefined;
 const npiIdentifier = npi && /^\d{10}$/.test(npi)
  ? { "@type": "PropertyValue", propertyID: "NPI", value: npi }
  : undefined;

 const normalizedPhone = normalizePhoneToE164(p?.phone);

 const claimedBy = pa?.claimed_by as string | null | undefined;
 const isAcceptingPatientsRaw = pa?.is_accepting_patients as boolean | null | undefined;
 const acceptingPatientsField =
  claimedBy != null && isAcceptingPatientsRaw === true
   ? { isAcceptingPatients: true }
   : claimedBy != null && isAcceptingPatientsRaw === false
   ? { isAcceptingPatients: false }
   : {};

 const scripts = p
 ? [
  {
  type: "application/ld+json",
  children: JSON.stringify({
   "@context": "https://schema.org",
   "@type": providerSchemaType(p.specialty),
   "@id": `${url}#provider`,
   name: nameWithCreds,
   alternateName: pa.practice || undefined,
   description: pa.about || `${nameWithCreds} is a ${specialtyToTitle(p.specialty)} in ${p.city}${p.state ? `, ${p.state}` : ""}.`,
   image: photo || undefined,
   url,
   ...(normalizedPhone ? { telephone: normalizedPhone } : {}),
   ...(npiIdentifier ? { identifier: npiIdentifier } : {}),
   email: pa.email || undefined,
   medicalSpecialty: p.specialty,
   address: {
   "@type": "PostalAddress",
   streetAddress: p.address || undefined,
   addressLocality: p.city,
   addressRegion: p.state || undefined,
   postalCode: pa.zip || undefined,
   addressCountry: "US",
   },
   ...(pa.latitude && pa.longitude ? {
   geo: { "@type": "GeoCoordinates", latitude: pa.latitude, longitude: pa.longitude },
   } : {}),
   ...(aggregateRating ? { aggregateRating } : {}),
   ...(p.website ? { sameAs: [p.website] } : {}),
   ...acceptingPatientsField,
   ...(services.length > 0 ? {
   availableService: services.map((s) => ({ "@type": "MedicalProcedure", name: s })),
   } : {}),
   parentOrganization: { "@id": "https://heallexa.com/#organization" },
  }),
  },
  {
  type: "application/ld+json",
  children: JSON.stringify({
   "@context": "https://schema.org",
   "@type": "BreadcrumbList",
   "@id": `${url}#breadcrumb`,
   itemListElement: [
   { "@type": "ListItem", position: 1, name: "Home", item: "https://heallexa.com" },
   { "@type": "ListItem", position: 2, name: "Specialties", item: "https://heallexa.com/specialties" },
   { "@type": "ListItem", position: 3, name: p.specialty, item: `https://heallexa.com/specialties/${specSlug}` },
   { "@type": "ListItem", position: 4, name: `${p.specialty} in ${p.city}`, item: `https://heallexa.com/specialty/${specSlug}/${citySlug}` },
   { "@type": "ListItem", position: 5, name: nameWithCreds, item: url },
   ],
  }),
  },
 ]
 : [];

 return {
  meta,
  links: [{ rel: "canonical", href: url }],
  scripts,
 };
 },

 notFoundComponent: () => (
 <div className="min-h-screen bg-background">
 <Navbar />
 <div className="pt-40 pb-20 text-center">
 <h1 className="text-2xl font-semibold mb-2">Provider not found</h1>
 <p className="text-muted-foreground mb-6">This listing doesn't exist or was removed.</p>
 <Button asChild><Link to="/browse">Browse providers</Link></Button>
 </div>
 </div>
 ),
 errorComponent: ProviderErrorBoundary,
 component: ProviderPage,
});

type TabKey = "overview" | "services" | "reviews" | "social" | "location";

function isValidUrl(u?: string | null): u is string {
 if (!u) return false;
 const s = u.trim();
 if (!s || s === " " || s.toLowerCase() === "n/a") return false;
 try {
 const url = new URL(s.startsWith("http") ? s : `https://${s}`);
 return !!url.hostname && url.hostname.includes(".");
 } catch { return false; }
}

function ProviderPage() {
 const { provider: p, related: initialRelated } = Route.useLoaderData();
 const displayCreds = dedupeCredentials(p.name, (p as any).credentials);
 const nameWithCreds = displayCreds ? `${p.name}, ${displayCreds}` : p.name;
 const navigate = useNavigate();
 const { user, primaryRole, loading: authLoading } = useAuth();
 const [related, setRelated] = useState<Provider[]>(initialRelated);
 const [tab, setTab] = useState<TabKey>("overview");
 const [reviews, setReviews] = useState<Review[]>([]);
 const [reviewsLoading, setReviewsLoading] = useState(true);
 const [submitting, setSubmitting] = useState(false);
 const [newRating, setNewRating] = useState(5);
 const [newComment, setNewComment] = useState("");
 const [newName, setNewName] = useState("");
 const [funnelOpen, setFunnelOpen] = useState(false);
 const [funnelType, setFunnelType] = useState<"report" | "update" | "removal" | "is_this_you">("report");
 const [reviewPage, setReviewPage] = useState(1);

 useEffect(() => {
 let cancelled = false;
 searchProviders({ specialty: p.specialty, location: p.state ? `${p.city}, ${p.state}` : p.city, limit: 12 })
 .then((rows) => {
 if (cancelled) return;
 setRelated(rows.filter((r) => r.id !== p.id).slice(0, 6));
 })
 .catch(() => { /* non-fatal */ });
 return () => { cancelled = true; };
 }, [p.id, p.specialty, p.city]);

  const handleClaimClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    trackListingMetric(p.id, "claim_clicks");
    navigate({ to: "/claim", search: { providerId: p.id, plan: "free" } as any });
  };

 const claimed = p.listing_status !== "unclaimed";
 const premium =
 p.listing_status === "premium" ||
 p.plan === "premium_monthly" ||
 p.plan === "premium_yearly";
 const featured = p.is_featured && p.featured_subscription_status === "active";
 const verified = claimed;
 const identityVerified = !!(p as any).verified_badge;

 const openFunnel = (t: typeof funnelType) => { setFunnelType(t); setFunnelOpen(true); };

 useEffect(() => {
 let active = true;
 setReviewsLoading(true);
 getProviderReviews(p.id)
 .then((rs) => { if (active) setReviews(rs); })
 .catch(() => { if (active) setReviews([]); })
 .finally(() => { if (active) setReviewsLoading(false); });
 return () => { active = false; };
 }, [p.id]);

 const reviewCount = reviews.length;
 const ratingValue = reviewCount > 0
 ? reviews.reduce((s, r) => s + r.rating, 0) / reviewCount
 : Number(p.rating ?? 0);
 const REVIEWS_PER_PAGE = 5;
 const totalReviewPages = Math.max(1, Math.ceil(reviewCount / REVIEWS_PER_PAGE));
 const pagedReviews = reviews.slice(
 (reviewPage - 1) * REVIEWS_PER_PAGE,
 reviewPage * REVIEWS_PER_PAGE,
 );

 const googlePlaceId = (p as any).google_place_id as string | null | undefined;
 const googleReviewsUrl: string | null =
 (p as any).google_reviews_url ??
 (googlePlaceId ? `https://search.google.com/local/reviews?placeid=${googlePlaceId}` : null);

 const handleSubmitReview = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!user) {
 toast.error("Please sign in to leave a review.");
 navigate({ to: "/login" });
 return;
 }
 if (!newComment.trim()) {
 toast.error("Please write a short comment.");
 return;
 }
 setSubmitting(true);
 try {
 const created = await submitReview({
 providerId: p.id,
 rating: newRating,
 comment: newComment.trim(),
 reviewerName: newName.trim() || (user.email?.split("@")[0] ?? "Patient"),
 });
 setReviews((rs) => [created, ...rs]);
 setNewComment("");
 setNewRating(5);
 toast.success("Review posted. Thank you!");
 } catch (err: any) {
 toast.error(err?.message ?? "Could not submit review.");
 } finally {
 setSubmitting(false);
 }
 };

 const handleDeleteReview = async (reviewId: string) => {
 try {
 await deleteReview(reviewId);
 setReviews((rs) => rs.filter((r) => r.id !== reviewId));
 toast.success("Review removed.");
 } catch (err: any) {
 toast.error(err?.message ?? "Could not delete review.");
 }
 };

 const websiteValid = isValidUrl(p.website);
 const websiteHref = websiteValid ? (p.website!.startsWith("http") ? p.website! : `https://${p.website}`) : null;

 useEffect(() => { trackListingMetric(p.id, "profile_views"); }, [p.id]);

 const initials = p.name
 .replace(/^Dr\.\s*/i, "").split(/[ ,]+/).filter(Boolean).slice(0, 2)
 .map((n: string) => n[0]).join("").toUpperCase();

 const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(p.address)}`;
 const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(p.address)}&output=embed`;

 const tabs: Array<{ key: TabKey; label: string }> = premium
 ? [
 { key: "overview", label: "Overview" },
 { key: "services", label: "Services" },
 { key: "reviews", label: `Reviews${reviewCount > 0 ? ` (${reviewCount})` : ""}` },
 { key: "social", label: "Social" },
 { key: "location", label: "Location" },
 ]
 : [
 { key: "overview", label: "Overview" },
 { key: "location", label: "Location" },
 ];

 useEffect(() => {
 if (!premium && (tab === "services" || tab === "reviews" || tab === "social")) {
 setTab("overview");
 }
 }, [premium, tab]);

 const citySlug = p.city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
 const specSlug = p.specialty.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
 const stateLower = p.state ? p.state.toLowerCase() : "";
 // stateSlug uses the full state name slug ("texas" not "tx") to match the
 // /locations/$state route canonical form and prevent non-canonical duplicate
 // URLs that dilute link equity across 6.3M provider pages.
 const stateSlug = p.state ? stateSlugFromCode(p.state) : "";

 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />

 {/* BREADCRUMBS */}
 <nav aria-label="Breadcrumb" className="pt-20 pb-2 border-b border-border/60 bg-background">
 <div className="mx-auto max-w-6xl px-6">
 <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
 <li><Link to="/" className="hover:text-primary">Home</Link></li>
 <li aria-hidden>/</li>
 <li><Link to="/specialties" className="hover:text-primary">Specialties</Link></li>
 <li aria-hidden>/</li>
 <li>
 <a href={`/specialty/${specSlug}/${citySlug}`} className="hover:text-primary">
 {p.specialty} in {p.city}
 </a>
 </li>
 {stateSlug && (
 <>
 <li aria-hidden>/</li>
 <li><a href={`/locations/${stateSlug}`} className="hover:text-primary">{p.state}</a></li>
 </>
 )}
 <li aria-hidden>/</li>
 <li className="text-foreground font-medium truncate max-w-[200px]">{p.name}</li>
 </ol>
 </div>
 </nav>

 {/* HEADER */}
 <header className="pt-6 pb-6 border-b border-border bg-card">
 <div className="mx-auto max-w-6xl px-6">
 <div className="flex flex-col md:flex-row gap-5 md:items-center">
 <ProviderPhoto provider={p} className="h-20 w-20 md:h-24 md:w-24 rounded-md border border-border shrink-0" />

 <div className="flex-1 min-w-0">
 <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
 {featured && (
 <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-primary text-primary-foreground font-semibold rounded-none">
 Featured
 </span>
 )}
 {identityVerified ? (
 <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 rounded-none">
 <ShieldCheck className="h-3 w-3" /> Identity Verified
 </span>
 ) : verified ? (
 <span className="inline-flex items-center gap-1 text-[10px] text-primary font-semibold border border-primary/40 px-2 py-0.5 rounded-none">
 <CheckCircle2 className="h-3 w-3" /> Verified
 </span>
 ) : (
 <span className="text-[10px] text-muted-foreground border border-border px-2 py-0.5 rounded-none">
 Unclaimed
 </span>
 )}
 {(() => { const npi = p.npi?.trim(); return npi && /^\d{10}$/.test(npi) ? (
 <a
 href={`https://npiregistry.cms.hhs.gov/provider-view/${npi}`}
 target="_blank"
 rel="noreferrer"
 className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground border border-border px-2 py-0.5 hover:border-primary/40 hover:text-primary transition-colors"
 >
 NPI&nbsp;·&nbsp;{npi}
 </a>
 ) : null; })()}
 </div>

 <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-foreground leading-tight">
 {nameWithCreds}
 </h1>
 <p className="mt-0.5 text-sm md:text-base font-medium text-foreground/80">
 {p.specialty}
 </p>
 <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
 <MapPin className="h-3.5 w-3.5 shrink-0" />
 <span className="truncate">
 {[p.address, p.city, p.state].filter(Boolean).join(", ")}{(p as any).zip ? ` ${(p as any).zip}` : ""}
 </span>
 </p>

 <div className="mt-2 text-xs">
 {reviewCount > 0 ? (
 <span className="inline-flex items-center gap-1.5">
 <Star className="h-3.5 w-3.5 fill-primary text-primary" />
 <span className="font-semibold">{ratingValue.toFixed(2)}</span>
 <span className="text-muted-foreground">({reviewCount} review{reviewCount === 1 ? "" : "s"})</span>
 </span>
 ) : (
 <span className="text-muted-foreground">No reviews yet</span>
 )}
 </div>
 </div>

 <div className="grid grid-cols-2 gap-2 w-full md:w-auto md:flex md:flex-row md:min-w-[180px] md:self-center">
 <Button asChild size="sm" className="rounded-none" onClick={() => trackListingMetric(p.id, "phone_clicks")}>
 <a href={`tel:${p.phone}`} className="!flex items-center justify-center gap-2">
 <Phone className="h-4 w-4" /> Call
 </a>
 </Button>
 {websiteHref && (
 <Button variant="outline" size="sm" asChild className="rounded-none" onClick={() => trackListingMetric(p.id, "website_clicks")}>
 <a href={websiteHref} target="_blank" rel="noreferrer" className="!flex items-center justify-center gap-2">
 <Globe className="h-4 w-4" /> Website
 </a>
 </Button>
 )}
 <Button variant="outline" size="sm" asChild className="rounded-none">
 <a href={directionsUrl} target="_blank" rel="noreferrer" className="!flex items-center justify-center gap-2">
 <Navigation className="h-4 w-4" /> Directions
 </a>
 </Button>
 <Button variant="outline" size="sm" asChild className="rounded-none col-span-2 md:col-span-1" title="Verify this provider's credentials on DocInfo.org (Federation of State Medical Boards)">
 <a href="https://www.docinfo.org/" target="_blank" rel="noopener noreferrer nofollow" className="!flex items-center justify-center gap-2">
 <ShieldCheck className="h-4 w-4" /> Verify Credentials <ExternalLink className="h-3 w-3 opacity-70" />
 </a>
 </Button>
 </div>
 </div>
 </div>
 </header>

 {/* TABS */}
 <div className="border-b border-border bg-background sticky top-0 z-30">
 <div className="mx-auto max-w-6xl px-6">
 <div className="flex gap-6 overflow-x-auto">
 {tabs.map((t) => (
 <button
 key={t.key}
 onClick={() => setTab(t.key)}
 className={`py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
 tab === t.key
 ? "border-primary text-foreground"
 : "border-transparent text-muted-foreground hover:text-foreground"
 }`}
 >
 {t.label}
 </button>
 ))}
 </div>
 </div>
 </div>

 {/* MAIN GRID */}
 <main className="mx-auto max-w-6xl px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
 <section className="lg:col-span-2 space-y-6">
 {tab === "overview" && (
 <>
 <Block title="Availability">
  <MonthAvailabilityCalendar
 availableDays={(p as any).available_days ?? ["mon", "tue", "wed", "thu", "fri"]}
 providerId={p.id}
 providerName={p.name}
 services={p.services ?? []}
 insuranceIds={(p.insurance as string[]) ?? []}
 bookingEnabled={claimed && premium}
 phone={p.phone}
 />
 </Block>

 <Block title="About">
 <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
 {p.about || `${nameWithCreds} is a ${p.specialty} provider located in ${p.city}${p.state ? `, ${p.state}` : ""}.`}
 </p>
 </Block>

 <Block title="Contact information">
 <div className="divide-y divide-border border border-border bg-card">
 <InfoRow label="Phone" value={
  <a href={`tel:${p.phone}`} className="text-primary hover:underline">{p.phone}</a>
 } />
 {(p as any).fax && (
  <InfoRow label="Fax" value={<span className="text-foreground">{(p as any).fax}</span>} />
 )}
 {websiteHref && (
 <InfoRow label="Website" value={
 <a href={websiteHref} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">
 {websiteHref.replace(/^https?:\/\//, "").replace(/\/$/, "")}
 </a>
 } />
 )}
 {googleReviewsUrl && (
 <InfoRow label="Google" value={
 <a href={googleReviewsUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">
 View Google Business Profile
 </a>
 } />
 )}
 <InfoRow label="Address" value={p.address} />
 {(p as any).gender === "male" || (p as any).gender === "female" ? (
 <InfoRow label="Sex" value={(p as any).gender === "female" ? "Female" : "Male"} />
 ) : null}
 {(p as any).license_number && (
 <InfoRow
 label="License"
 value={
 <span>
 {(p as any).license_number}
 {(p as any).license_state ? ` · ${(p as any).license_state}` : ""}
 </span>
 }
 />
 )}
 </div>
 </Block>

 {premium && <OverviewSocialBlock provider={p} googleReviewsUrl={googleReviewsUrl} />}

 {premium && p.languages.length > 0 && (
 <Block title="Languages spoken">
 <div className="flex flex-wrap gap-2">
 {(p.languages as string[]).map((l: string) => (
 <span key={l} className="px-3 py-1 text-sm border border-border bg-card">{l}</span>
 ))}
 </div>
 </Block>
 )}

 {premium && (
 <Block title="Insurance accepted">
 {p.insurance.length > 0 ? (
 <div className="flex flex-wrap gap-2">
 {(p.insurance as string[]).map((id: string) => {
 const ins = findInsurance(id);
 if (!ins) return null;
 return (
 <span
 key={id}
 className="px-3 py-1.5 text-sm border font-medium"
 style={{ borderColor: ins.color, color: ins.color }}
 >
 {ins.short}
 </span>
 );
 })}
 </div>
 ) : (
 <p className="text-sm text-muted-foreground italic">No insurance information provided.</p>
 )}
 </Block>
 )}

 <Block title="Office hours">
 {p.hours ? (
  <div className="border border-border bg-card divide-y divide-border">
  {p.hours.split(/\n|;|\|/).map((s: string) => s.trim()).filter(Boolean).map((line: string, i: number) => (
   <div key={i} className="px-4 py-2.5 text-sm flex justify-between gap-4">
   <span className="text-foreground">{line}</span>
   </div>
  ))}
  </div>
 ) : (
  <div className="border border-dashed border-border bg-muted/30 px-6 py-6 text-center">
  <p className="text-sm font-medium text-foreground">Hours not yet listed</p>
  <p className="text-xs text-muted-foreground mt-1 mb-3">
   Are you this provider? Claim this listing to add office hours,
   photos, and contact details.
  </p>
  <Button size="sm" className="rounded-none" onClick={handleClaimClick}>
   Claim listing to add hours
  </Button>
  </div>
 )}
 </Block>
 </>
 )}

 {tab === "services" && (
 <Block title="Services offered">
 {p.services.length > 0 ? (
 <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
 {(p.services as string[]).map((s: string) => (
 <li key={s} className="flex items-start gap-2 text-sm py-1.5 border-b border-border/60">
 <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
 <span>{s}</span>
 </li>
 ))}
 </ul>
 ) : (
 <EmptyState
 title="No services listed yet"
 body={
 claimed
 ? "The provider has not added a service list yet."
 : "This provider has not added services yet. Claim this listing to add services and attract more patients."
 }
 showClaim={!claimed}
 onClaim={handleClaimClick}
 />
 )}
 </Block>
 )}

 {tab === "reviews" && (
 <Block title="Patient reviews">
 {googleReviewsUrl && (
 <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-border bg-card px-4 py-3">
 <div className="text-sm">
 <p className="font-medium">Google Business Profile</p>
 <p className="text-muted-foreground text-xs">
 {(p as any).google_review_count
 ? `${(p as any).google_review_count} reviews on Google${(p as any).google_rating ? ` · ${Number((p as any).google_rating).toFixed(1)}\u2605` : ""}`
 : "See this provider's reviews on Google."}
 </p>
 </div>
 <Button asChild variant="outline" size="sm" className="rounded-none">
 <a href={googleReviewsUrl} target="_blank" rel="noreferrer">View on Google</a>
 </Button>
 </div>
 )}

 {reviewsLoading ? (
 <p className="text-sm text-muted-foreground">Loading reviews\u2026</p>
 ) : reviewCount > 0 ? (
 <>
 <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border">
 <Star className="h-6 w-6 fill-primary text-primary" />
 <span className="text-2xl font-semibold">{ratingValue.toFixed(2)}</span>
 <span className="text-muted-foreground">\u00b7 {reviewCount} review{reviewCount === 1 ? "" : "s"}</span>
 </div>
 <ul className="space-y-5">
 {pagedReviews.map((r) => {
 const isOwn = !!user && r.user_id === user.id;
 const dateStr = new Date(r.review_date).toLocaleDateString("en-US");
 return (
 <li key={r.id} className="border border-border bg-card p-4">
 <div className="flex items-center justify-between mb-1 gap-2">
 <span className="font-semibold text-sm flex items-center gap-2">
 {r.reviewer_name}
 {r.source === "google" && (
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5">
 Google
 </span>
 )}
 </span>
 <div className="flex items-center gap-2">
 <span className="text-xs text-muted-foreground">{dateStr}</span>
 {isOwn && (
 <button
 onClick={() => handleDeleteReview(r.id)}
 className="text-muted-foreground hover:text-destructive"
 aria-label="Delete review"
 >
 <Trash2 className="h-3.5 w-3.5" />
 </button>
 )}
 </div>
 </div>
 <div className="flex items-center gap-1 mb-2">
 {Array.from({ length: 5 }).map((_, idx) => (
 <Star key={idx} className={`h-3.5 w-3.5 ${idx < r.rating ? "fill-primary text-primary" : "text-muted"}`} />
 ))}
 </div>
 {r.comment && <p className="text-sm text-muted-foreground whitespace-pre-line">{r.comment}</p>}
 {r.review_url && (
 <a href={r.review_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline mt-2 inline-block">
 View original
 </a>
 )}
 </li>
 );
 })}
 </ul>
 {totalReviewPages > 1 && (
 <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
 <span className="text-xs text-muted-foreground">
 Page {reviewPage} of {totalReviewPages}
 </span>
 <div className="flex items-center gap-2">
 <Button
 variant="outline"
 size="sm"
 onClick={() => setReviewPage((p) => Math.max(1, p - 1))}
 disabled={reviewPage === 1}
 >
 <ChevronLeft className="h-4 w-4" /> Previous
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={() => setReviewPage((p) => Math.min(totalReviewPages, p + 1))}
 disabled={reviewPage === totalReviewPages}
 >
 Next <ChevronRight className="h-4 w-4" />
 </Button>
 </div>
 </div>
 )}
 </>
 ) : (
 <EmptyState
 title="No reviews yet"
 body="Be the first to share your experience after visiting this provider."
 />
 )}

 <form onSubmit={handleSubmitReview} className="mt-8 border-t border-border pt-6 space-y-3">
 <h3 className="font-semibold">Write a review</h3>
 {!user && (
 <p className="text-xs text-muted-foreground">
 You'll be asked to sign in before posting.
 </p>
 )}
 <div className="flex items-center gap-1">
 {Array.from({ length: 5 }).map((_, idx) => {
 const value = idx + 1;
 return (
 <button
 key={idx}
 type="button"
 onClick={() => setNewRating(value)}
 aria-label={`${value} star${value > 1 ? "s" : ""}`}
 >
 <Star className={`h-6 w-6 ${value <= newRating ? "fill-primary text-primary" : "text-muted"}`} />
 </button>
 );
 })}
 </div>
 <Input
 placeholder="Display name (optional)"
 value={newName}
 onChange={(e) => setNewName(e.target.value)}
 className="rounded-none"
 />
 <Textarea
 placeholder="Share details about your experience\u2026"
 value={newComment}
 onChange={(e) => setNewComment(e.target.value)}
 rows={4}
 className="rounded-none"
 required
 maxLength={2000}
 />
 <Button type="submit" className="rounded-none" disabled={submitting}>
 {submitting ? "Posting\u2026" : "Post review"}
 </Button>
 </form>
 </Block>
 )}

 {tab === "social" && <SocialProfilesBlock provider={p} googleReviewsUrl={googleReviewsUrl} />}

 {tab === "location" && (
 <Block title="Office location">
 <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1.5">
 <MapPin className="h-4 w-4" /> {p.address}
 </p>
 <div className="aspect-[16/9] w-full border border-border bg-muted overflow-hidden mb-4">
 <iframe
 src={mapEmbed}
 title={`Map of ${p.practice}`}
 className="w-full h-full"
 loading="lazy"
 referrerPolicy="no-referrer-when-downgrade"
 />
 </div>
 <Button variant="outline" asChild className="rounded-none">
 <a href={directionsUrl} target="_blank" rel="noreferrer" className="!flex items-center justify-center gap-2">
 <Navigation className="h-4 w-4" /> Get directions
 </a>
 </Button>
 </Block>
 )}
 </section>

 {/* SIDEBAR */}
 <aside className="space-y-4">
 {!claimed ? (
 <SidebarBox tone="warning" title="This listing is unclaimed">
 <p className="text-sm text-muted-foreground mb-4">
 Are you the owner? Claim this profile to manage your information and respond to patients.
 </p>
 <Button className="w-full rounded-none" onClick={handleClaimClick}>
 Claim this listing
 </Button>
 <button
 onClick={() => openFunnel("is_this_you")}
 className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
 >
 Is this you? Verify by email
 </button>
 </SidebarBox>
 ) : (
 <SidebarBox tone="success" title={identityVerified ? "Identity Verified listing" : "Claimed listing"}>
 <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
 <ShieldCheck className="h-4 w-4 text-primary" />
 {identityVerified ? "Provider identity verified" : "Managed by the provider"}
 </p>
 </SidebarBox>
 )}

 {!claimed ? (
 <SidebarBox title="Manage this listing">
 <p className="text-sm text-muted-foreground">
 Claim this listing to manage and update your profile.
 </p>
 </SidebarBox>
 ) : !premium ? (
 <SidebarBox title="Upgrade your listing">
 <p className="text-sm text-muted-foreground mb-3">
 Upgrade your listing to unlock full profile features, photos, and booking.
 </p>
 <Button asChild variant="outline" className="w-full rounded-none">
 <Link to="/dashboard/doctor" search={{ billing: undefined, pid: undefined, tab: undefined, verified: false, verificationSessionId: undefined, status: undefined }}>Manage in dashboard</Link>
 </Button>
 </SidebarBox>
 ) : null}

 <SidebarBox title="Listing details">
 <div className="space-y-2.5 text-sm">
 <Row label="Last updated">{new Date(p.updated_at).toLocaleDateString("en-US", { timeZone: "UTC" })}</Row>
 <Row label="Listing ID"><span className="font-mono text-xs">{(p as any).public_id ?? p.id.slice(0, 8)}</span></Row>
 </div>
 <div className="mt-4 pt-4 border-t border-border space-y-2">
 <button
 onClick={() => openFunnel("update")}
 className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 py-2 border border-border bg-background"
 >
 Suggest an edit
 </button>
 <button
 onClick={() => openFunnel("report")}
 className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 py-2 border border-border bg-background"
 >
 <Flag className="h-3 w-3" /> Report listing
 </button>
 <button
 onClick={() => openFunnel("removal")}
 className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 py-2 border border-border bg-background"
 >
 Request removal
 </button>
 </div>
 </SidebarBox>
 <ProviderTrustFunnel
 providerId={p.id}
 open={funnelOpen}
 onOpenChange={setFunnelOpen}
 type={funnelType}
 />
 </aside>
 </main>

 {related.length > 0 && (
 <section className="border-t border-border bg-background">
 <div className="mx-auto max-w-6xl px-6 py-12">
 <h2 className="text-2xl font-semibold tracking-tight mb-1">
 More {p.specialty.toLowerCase()} in {p.city}
 </h2>
 <p className="text-sm text-muted-foreground mb-6">Other providers patients also viewed.</p>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
 {related.map((r: Provider) => <RelatedCard key={r.id} p={r} />)}
 </div>
 </div>
 </section>
 )}

 {/* RELATED SEARCHES */}
 <section className="border-t border-border bg-card/40">
 <div className="mx-auto max-w-6xl px-6 py-12">
 <h2 className="text-lg font-semibold tracking-tight mb-4">Related searches</h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
 <div>
 <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">By specialty</p>
 <ul className="space-y-1.5">
 <li><a href={`/specialty/${specSlug}/${citySlug}`} className="text-primary hover:underline">{p.specialty} in {p.city}</a></li>
 {stateLower && (
 <li><a href={`/specialty/${specSlug}/state/${stateLower}`} className="text-primary hover:underline">{p.specialty} in {p.state}</a></li>
 )}
 <li><Link to="/specialties" className="text-primary hover:underline">Browse all specialties</Link></li>
 </ul>
 </div>
 <div>
 <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">By location</p>
 <ul className="space-y-1.5">
 {stateSlug && (
 <li><a href={`/locations/${stateSlug}`} className="text-primary hover:underline">Providers in {p.state}</a></li>
 )}
 <li><Link to="/locations" className="text-primary hover:underline">Browse all locations</Link></li>
 <li><Link to="/sitemap" className="text-primary hover:underline">Provider directory</Link></li>
 </ul>
 </div>
 <div>
 <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">More from Heallexa</p>
 <ul className="space-y-1.5">
 <li><Link to="/insurance" className="text-primary hover:underline">Browse by insurance</Link></li>
 <li><Link to="/list-your-practice" className="text-primary hover:underline">List your practice</Link></li>
 <li><Link to="/browse" className="text-primary hover:underline">Find a provider</Link></li>
 </ul>
 </div>
 </div>
 </div>
 </section>

 {/* MEDICAL DISCLAIMER */}
 <section className="border-t border-border bg-background">
 <div className="mx-auto max-w-6xl px-6 py-6">
 <p className="text-xs text-muted-foreground leading-relaxed">
 <strong className="text-foreground">Disclaimer:</strong> The information on this page is provided for general
 informational purposes and is not medical advice. Heallexa does not endorse any specific provider, treatment,
 or procedure. Always consult a qualified healthcare professional for medical questions and verify credentials,
 licensure, and availability directly with the provider before scheduling care.
 </p>
 </div>
 </section>

 <FooterSection />
 </div>
 );
}

type SocialLinkItem = {
 key: string;
 label: string;
 href: string | null;
 Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
 handle?: string;
};

function providerSocialLinks(provider: Provider, googleReviewsUrl: string | null, includeGoogleLabel = false): SocialLinkItem[] {
 const baseLinks = [
 { key: "linkedin", label: "LinkedIn", href: normalizeUrl((provider as any).linkedin_url), Icon: Linkedin },
 { key: "facebook", label: "Facebook", href: normalizeUrl((provider as any).facebook_url), Icon: Facebook },
 { key: "instagram", label: "Instagram", href: normalizeUrl((provider as any).instagram_url), Icon: Instagram },
 { key: "tiktok", label: "TikTok", href: normalizeUrl((provider as any).tiktok_url), Icon: TikTokIcon },
 { key: "youtube", label: "YouTube", href: normalizeUrl((provider as any).youtube_url), Icon: Youtube },
 { key: "x", label: includeGoogleLabel ? "X (Twitter)" : "X", href: normalizeUrl((provider as any).x_url), Icon: XIcon },
 ];
 const googleLink = { key: "google", label: includeGoogleLabel ? "Google My Business" : "Google", href: googleReviewsUrl, Icon: Globe, handle: "View profile on Google" };
 return (includeGoogleLabel ? [googleLink, ...baseLinks] : [...baseLinks, googleLink]).filter((item) => !!item.href);
}

function OverviewSocialBlock({ provider, googleReviewsUrl }: { provider: Provider; googleReviewsUrl: string | null }) {
 const socials = providerSocialLinks(provider, googleReviewsUrl);
 if (socials.length === 0) return null;
 return (
 <Block title="Social profiles">
 <div className="flex flex-wrap gap-2">
 {socials.map(({ key, label, href, Icon }) => (
 <a
 key={key}
 href={href!}
 target="_blank"
 rel="noreferrer"
 className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border bg-card hover:border-primary hover:text-primary transition-colors"
 >
 <Icon className="h-4 w-4" />
 {label}
 </a>
 ))}
 </div>
 </Block>
 );
}

function SocialProfilesBlock({ provider, googleReviewsUrl }: { provider: Provider; googleReviewsUrl: string | null }) {
 const socials = providerSocialLinks(provider, googleReviewsUrl, true);
 const trackSocial = (platform: string) => {
 try {
 void trackSocialClickFn({ data: { providerId: provider.id, platform } }).catch(() => {});
 } catch { /* fire-and-forget */ }
 };
 return (
 <Block title="Social profiles & Google My Business">
 {socials.length > 0 ? (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 {socials.map(({ key, label, href, Icon, handle }) => (
 <a
 key={key}
 href={href!}
 target="_blank"
 rel="noreferrer"
 onClick={() => trackSocial(key)}
 className="flex items-center gap-3 px-4 py-3 border border-border bg-card hover:border-primary hover:text-primary transition-colors"
 >
 <Icon className="h-5 w-5 shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium">{label}</div>
 <div className="text-xs text-muted-foreground truncate">
 {handle ?? href!.replace(/^https?:\/\//, "").replace(/\/$/, "")}
 </div>
 </div>
 </a>
 ))}
 </div>
 ) : (
 <EmptyState
 title="No social profiles linked yet"
 body="The provider has not added any social media or Google My Business links."
 />
 )}
 </Block>
 );
}

/* ---------- Availability Calendar ---------- */

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function isAvailable(date: Date, availableDays: string[]) {
 const key = DAY_KEYS[date.getDay()];
 return availableDays?.map((d) => d.toLowerCase()).includes(key);
}

const TIME_SLOTS = [
 "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
 "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
];

function MonthAvailabilityCalendar({
 availableDays,
 providerId,
 providerName,
 services,
 insuranceIds,
 bookingEnabled = true,
 phone,
}: {
 availableDays: string[];
 providerId: string;
 providerName: string;
 services: string[];
 insuranceIds: string[];
 bookingEnabled?: boolean;
 phone?: string;
}) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string>(services[0] ?? "General consultation");
  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const today = new Date(); today.setHours(0,0,0,0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const data = await loadProviderAvailabilityFn({ data: { providerId } });
      if (!cancelled) setWindows((data ?? []) as AvailabilityWindow[]);
    };
    load();
    const interval = setInterval(load, 20_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [providerId]);

 const slotTaken = (date: Date, slot: string) => {
 const seed = `${providerId}-${date.toDateString()}-${slot}`;
 let h = 0;
 for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
 return h % 5 === 0;
 };

  const persistBooking = async (date: Date, slot: string, service: string, notes?: string | null) => {
  if (!user?.email) return false;
  const [hh, mm] = slot.split(":").map(Number);
  const apptAt = new Date(date);
  apptAt.setHours(hh, mm, 0, 0);
  const customerName =
  (user.user_metadata?.full_name as string | undefined) ||
  [user.user_metadata?.first_name, user.user_metadata?.last_name].filter(Boolean).join(" ").trim() ||
  user.email!;
  let inserted: { id: string | null } | null = null;
  try {
   inserted = await createBookingFn({
   data: {
   providerId,
   customerName,
   customerPhone: (user.user_metadata?.phone as string | undefined) ?? null,
   serviceRequested: service,
   appointmentAtISO: apptAt.toISOString(),
   notes: notes ?? null,
   },
   });
  } catch (err: any) {
   toast.error("Could not save your appointment", { description: err?.message });
   return false;
  }
   if (!inserted?.id) {
   toast.error("Could not save your appointment");
   return false;
   }
   try {
     const { notifyProviderMasked } = await import("@/lib/booking-notification.functions");
     void notifyProviderMasked({ data: { appointmentId: inserted.id } }).catch(() => {});
   } catch { /* ignore */ }
   return true;
   };

 const handleConfirm = async () => {
 if (!selected || !selectedSlot || authLoading) return;
 const dateLabel = selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
 const isoDate = selected.toISOString().slice(0, 10);

 if (!user) {
 if (typeof window !== "undefined") {
 const intent = {
 providerId, providerName, date: isoDate,
 slot: selectedSlot, service: selectedService,
 };
 localStorage.setItem("heallexa_pending_booking", JSON.stringify(intent));
 localStorage.setItem("heallexa_post_login_redirect", `/providers/${providerId}?book=1`);
 localStorage.setItem("selected_login_role", "patient");
 localStorage.setItem("heallexa_signup_account_type", "patient");
 }
 toast.message("Sign in to confirm your booking", {
 description: `${dateLabel} at ${selectedSlot}, ${selectedService}`,
 });
 navigate({ to: "/login/patient" });
 return;
 }

 trackListingMetric(providerId, "booking_clicks");
 const ok = await persistBooking(selected, selectedSlot, selectedService);
 if (!ok) return;

 toast.success(`Appointment booked, ${dateLabel} at ${selectedSlot}`, {
 description: `${selectedService} with ${providerName}. Confirmation email on its way.`,
 });
 setSelectedSlot(null);
 };

 useEffect(() => {
 if (authLoading || !user || typeof window === "undefined") return;
 const raw = localStorage.getItem("heallexa_pending_booking");
 if (!raw) return;
 let cancelled = false;
 (async () => {
 try {
 const intent = JSON.parse(raw);
 if (intent.providerId !== providerId) return;
 localStorage.removeItem("heallexa_pending_booking");
 const d = new Date(intent.date);
 if (isNaN(d.getTime())) return;
 d.setHours(0, 0, 0, 0);
 if (cancelled) return;
 setSelected(d);
 setSelectedSlot(intent.slot);
 if (intent.service) setSelectedService(intent.service);
 const dateLabel = d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
 trackListingMetric(providerId, "booking_clicks");
 const ok = await persistBooking(d, intent.slot, intent.service ?? "General consultation", intent.notes ?? null);
 if (!ok) return;
 toast.success(`Appointment booked, ${dateLabel} at ${intent.slot}`, {
 description: `${intent.service ?? "Appointment"} with ${providerName}. Confirmation email on its way.`,
 });
 } catch {}
 })();
 return () => { cancelled = true; };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user, authLoading, providerId, providerName]);

  const slotsForDay = (d: Date): string[] => {
    const dow = d.getDay();
    const todays = (windows ?? []).filter((w) => w.is_active && w.day_of_week === dow);
    if (todays.length === 0) {
      const key = DAY_KEYS[dow];
      const inAvail = availableDays?.map((x) => x.toLowerCase()).includes(key);
      if (inAvail) return [...TIME_SLOTS];
      if (!bookingEnabled && dow !== 0 && dow !== 6) return [...TIME_SLOTS];
      return [];
    }
    const set = new Set<string>();
    for (const w of todays) {
      const toMin = (t: string) => { const [h, m] = t.split(":").map(Number); return (h||0)*60+(m||0); };
      const pad = (n: number) => String(n).padStart(2, "0");
      const step = Math.max(5, w.slot_duration_minutes || 30);
      const start = toMin(w.start_time);
      const end = toMin(w.end_time);
      for (let m = start; m + step <= end; m += step) set.add(`${pad(Math.floor(m/60))}:${pad(m%60)}`);
    }
    return Array.from(set).sort();
  };

  const fmt12 = (slot: string) => {
    const [hh, mm] = slot.split(":").map(Number);
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${String(mm).padStart(2, "0")} ${hh >= 12 ? "PM" : "AM"}`;
  };

  const isDayDisabled = (d: Date) => {
    if (d < today) return true;
    return slotsForDay(d).length === 0;
  };

  const activeDate = selected ?? undefined;
  const daySlotList = activeDate ? slotsForDay(activeDate) : [];
  const dayLabel = activeDate
    ? activeDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    : "Select a date";

  return (
    <div className="flex flex-col gap-5">
      <div className="border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] divide-y md:divide-y-0 md:divide-x divide-border">
          <div className="p-4 sm:p-6">
            <div className="text-base font-semibold text-foreground mb-3">Select a date &amp; time</div>
            <ShadCalendar
              mode="single"
              selected={selected}
              onSelect={(d) => { setSelected(d ?? undefined); setSelectedSlot(null); }}
              disabled={isDayDisabled}
              fromDate={today}
              showOutsideDays={false}
              className="pointer-events-auto w-full [--cell-size:2.5rem]"
            />
          </div>
          <div className="p-4 sm:p-6 bg-card">
            <div className="text-sm font-semibold text-foreground mb-3">{dayLabel}</div>
            {!activeDate ? (
              <p className="text-sm text-muted-foreground">Pick a day on the calendar to see open times.</p>
            ) : daySlotList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No times available this day.</p>
            ) : (
              <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto pr-1">
                {!bookingEnabled && (
                  <p className="text-[11px] text-muted-foreground mb-1 italic">
                    Online booking not enabled for this provider \u2014 see contact info below.
                  </p>
                )}
                {daySlotList.map((s) => {
                  const isSel = selectedSlot === s;
                  const disabled = !bookingEnabled;
                  return (
                    <div key={s} className="flex gap-2">
                      <button
                        type="button"
                        disabled={disabled}
                        aria-disabled={disabled}
                        onClick={() => { if (!disabled) setSelectedSlot(s); }}
                        className={cn(
                          "flex-1 bg-white border-2 text-sm font-bold py-2.5 rounded-lg transition-all",
                          disabled
                            ? "border-border text-muted-foreground cursor-not-allowed"
                            : isSel
                              ? "border-primary text-primary ring-2 ring-primary/20"
                              : "border-primary/40 text-primary hover:border-primary"
                        )}
                      >
                        {fmt12(s)}
                      </button>
                      {isSel && !disabled && (
                        <Button
                          size="sm"
                          className="px-4 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm rounded-none"
                          onClick={() => {
                            trackListingMetric(providerId, "booking_clicks");
                            setBookingOpen(true);
                          }}
                        >
                          Confirm
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {!bookingEnabled && (
        <div className="border border-border bg-muted/30 p-4 rounded-md text-center">
          <p className="text-sm font-medium text-foreground">Online booking not available</p>
          <p className="mt-1 text-xs text-muted-foreground">
            This provider hasn't enabled online appointments. Please contact them directly to schedule.
          </p>
          {phone && (
            <Button
              asChild
              variant="outline"
              className="mt-3 rounded-md"
              onClick={() => trackListingMetric(providerId, "phone_clicks")}
            >
              <a href={`tel:${phone}`} className="!flex items-center justify-center gap-2">
                <Phone className="h-4 w-4" /> Call {phone}
              </a>
            </Button>
          )}
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="mt-2 rounded-md text-xs text-muted-foreground hover:text-foreground"
          >
            <Link to="/search" search={{ q: "Book an appointment", specialty: undefined, location: undefined, insurance: undefined }}>
              Ask the AI booking assistant for help \u2192
            </Link>
          </Button>
        </div>
      )}

      <BookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        providerId={providerId}
        providerName={providerName}
        providerInsuranceIds={insuranceIds}
        availableDays={availableDays}
        windows={windows}
        initialDate={selected ?? null}
        initialSlot={selectedSlot}
      />
    </div>
  );
}

/* ---------- helpers ---------- */

function Block({ title, children }: { title: string; children: React.ReactNode }) {
 return (
 <div className="border border-border bg-card p-6">
 <h2 className="text-lg font-semibold mb-4 text-foreground">{title}</h2>
 {children}
 </div>
 );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
 return (
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-4 py-3 text-sm">
 <span className="text-muted-foreground">{label}</span>
 <span className="text-foreground sm:text-right min-w-0 truncate">{value}</span>
 </div>
 );
}

function SidebarBox({
 title, children, tone,
}: {
 title: string; children: React.ReactNode;
 tone?: "success" | "warning";
}) {
 const toneClass =
 tone === "warning" ? "border-l-4 border-l-primary"
 : tone === "success" ? "border-l-4 border-l-primary/60"
 : "";
 return (
 <div className={`border border-border bg-card p-5 ${toneClass}`}>
 <h3 className="text-sm font-semibold mb-3 text-foreground">{title}</h3>
 {children}
 </div>
 );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
 return (
 <div className="flex items-center justify-between gap-4">
 <span className="text-muted-foreground">{label}</span>
 <span className="text-foreground">{children}</span>
 </div>
 );
}

function EmptyState({
 title, body, showClaim, onClaim,
}: { title: string; body: string; showClaim?: boolean; onClaim?: (e?: React.MouseEvent) => void }) {
 return (
 <div className="border border-dashed border-border bg-muted/30 px-6 py-10 text-center">
 <p className="text-sm font-medium text-foreground">{title}</p>
 <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">{body}</p>
 {showClaim && onClaim && (
 <Button className="mt-4 rounded-none" onClick={onClaim}>
 Claim this listing
 </Button>
 )}
 </div>
 );
}

function RelatedCard({ p }: { p: Provider }) {
 const claimed = p.listing_status !== "unclaimed";
 const premium = p.listing_status === "premium" || p.plan === "premium_monthly" || p.plan === "premium_yearly";
 const verified = claimed;
 const identityVerified = !!(p as any).verified_badge;
 const initials = p.name.replace(/^Dr\.\s*/i, "").split(/[ ,]+/).filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase();
 return (
 <Link
 to="/providers/$id"
 params={{ id: buildProviderSlug(p) }}
 className="block border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all group"
 >
 <div className="flex items-start gap-3">
 <div className="h-12 w-12 bg-primary/10 border border-border flex items-center justify-center text-sm font-bold text-primary shrink-0">
 {initials}
 </div>
 <div className="min-w-0 flex-1">
 <p className="font-semibold text-sm group-hover:text-primary transition-colors truncate">{p.name}</p>
 <p className="text-xs text-muted-foreground truncate">{p.practice}</p>
 <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
 <Building2 className="h-3 w-3" />
 <span className="truncate">{p.city}</span>
 </div>
 <div className="flex flex-wrap gap-1 mt-2">
 {identityVerified ? (
 <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 dark:text-emerald-400 border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5 font-medium">
 <ShieldCheck className="h-2.5 w-2.5" /> Identity Verified
 </span>
 ) : verified && (
 <span className="inline-flex items-center gap-0.5 text-[10px] text-primary border border-primary/30 px-1.5 py-0.5 font-medium">
 <CheckCircle2 className="h-2.5 w-2.5" /> Verified
 </span>
 )}
 {!claimed && (
 <span className="text-[10px] text-muted-foreground border border-border px-1.5 py-0.5">Unclaimed</span>
 )}
 </div>
 </div>
 </div>
 </Link>
 );
}
